import React from "react";
import PropTypes from "prop-types";
import styled from "styled-components";
import {
  FormattedMessage,
  defineMessages,
  useIntl,
  injectIntl
} from "react-intl";
import { Loading, Icon, Button } from "@onelogin/react-components";

import { Table } from "../../components/common/TableNew/Table/Table";
import { TableRow } from "../../components/common/TableNew/TableRow/TableRow";
import { TableData } from "../../components/common/TableNew/TableData/TableData";
import { TableHeader } from "../../components/common/TableNew/TableHeader/TableHeader";
import { TableHeaderRow } from "../../components/common/TableNew/TableHeaderRow/TableHeaderRow";
import Moment from "../MomentWrapper";

/* ----------------------------- Styled Components ----------------------------- */

const LoadingWrapper = styled.div`
  position: relative;
  width: 100%;
  height: 100%;
`;

const StyledButton = styled(Button)`
  min-width: 30px;
  width: 30px;
  height: 30px;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const TrashcanIcon = styled(Icon).attrs({
  icon: "Trashcan-24",
  size: "24"
})`
  cursor: pointer;
`;

const Container = styled.div`
  overflow-y: auto;
  height: 100%;
`;

const StyledTable = styled(Table)`
  margin-top: auto;
`;

const ACCESS_PRIVATE = 0;

/* ----------------------------- i18n Messages ----------------------------- */

const messages = defineMessages({
  deleteNote: {
    id: "deleteNote",
    defaultMessage: "Delete note"
  }
});

/* ----------------------------- Component ----------------------------- */

function SecureNotesTable({
  userId,
  notes,
  currentNote,
  locale,
  loading,
  showSecureNote,
  onClickRemove,
  showSecureNotePanel
}) {
  const intl = useIntl();

  const onRowClick = React.useCallback(
    (event, id) => {
      event.stopPropagation();
      showSecureNote(id);
    },
    [showSecureNote]
  );

  // If notes is undefined/null and we're loading, show spinner
  if (!notes && loading) {
    return (
      <LoadingWrapper>
        <Loading />
      </LoadingWrapper>
    );
  }

  // Normalize notes to an array for consistent rendering
  const safeNotes = notes ?? [];

  return (
    <Container>
      <StyledTable>
        <thead>
          <TableHeaderRow>
            <TableHeader style={{ minWidth: "100px" }}>
              <FormattedMessage
                defaultMessage="Secure Notes"
                id="secureNotes"
              />
            </TableHeader>

            {!showSecureNotePanel && (
              <>
                <TableHeader style={{ minWidth: "150px" }}>
                  <FormattedMessage
                    defaultMessage="Last Updated"
                    id="lastUpdated"
                  />
                </TableHeader>
                <TableHeader style={{ minWidth: "100px" }}>
                  <FormattedMessage defaultMessage="Status" id="status" />
                </TableHeader>
                <TableHeader style={{ minWidth: "100px" }}>
                  <FormattedMessage defaultMessage="Owner" id="owner" />
                </TableHeader>
                <TableHeader width="50px">
                  <FormattedMessage defaultMessage="Action" id="action" />
                </TableHeader>
              </>
            )}
          </TableHeaderRow>
        </thead>

        <tbody>
          {safeNotes.length === 0 && (
            <TableRow>
              <TableData
                colSpan={showSecureNotePanel ? 1 : 5}
                style={{ textAlign: "center", padding: "20px" }}
              >
                <FormattedMessage
                  id="noSecureNotes"
                  defaultMessage="No secure notes"
                />
              </TableData>
            </TableRow>
          )}

          {safeNotes.map(note => {
            const isCurrent = currentNote && currentNote.id === note.id;

            return (
              <TableRow
                key={note.id}
                onClick={event => onRowClick(event, note.id)}
                style={{
                  backgroundColor: isCurrent ? "#DDF0F6" : "transparent"
                }}
              >
                <TableData>{note.title}</TableData>

                {!showSecureNotePanel && (
                  <>
                    <TableData>
                      <Moment locale={locale} fromNow>
                        {note.updated_at}
                      </Moment>
                    </TableData>

                    <TableData>
                      {note.access === ACCESS_PRIVATE ? (
                        <FormattedMessage
                          defaultMessage="Private"
                          id="private"
                        />
                      ) : (
                        <FormattedMessage defaultMessage="Shared" id="shared" />
                      )}
                    </TableData>

                    <TableData>{note.owner}</TableData>

                    <TableData>
                      {note.user_id === userId && (
                        <StyledButton
                          aria-label={intl.formatMessage(messages.deleteNote)}
                          onClick={event => {
                            event.stopPropagation();
                            onClickRemove(event, note.id);
                          }}
                        >
                          <TrashcanIcon
                            data-testid={`noteslist-delete-${note.id}-button`}
                            alt={intl.formatMessage(messages.deleteNote)}
                          />
                        </StyledButton>
                      )}
                    </TableData>
                  </>
                )}
              </TableRow>
            );
          })}
        </tbody>
      </StyledTable>
    </Container>
  );
}

SecureNotesTable.propTypes = {
  notes: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.number.isRequired,
      title: PropTypes.string.isRequired,
      owner: PropTypes.string.isRequired,
      user_id: PropTypes.number.isRequired,
      updated_at: PropTypes.string.isRequired,
      access: PropTypes.number.isRequired
    })
  ),
  userId: PropTypes.number.isRequired,
  locale: PropTypes.string.isRequired,
  showSecureNote: PropTypes.func.isRequired,
  onClickRemove: PropTypes.func.isRequired,
  loading: PropTypes.bool.isRequired,
  showSecureNotePanel: PropTypes.bool.isRequired,
  currentNote: PropTypes.shape({
    id: PropTypes.number
  }),
};

export default injectIntl(SecureNotesTable);
